/**
 * Product Bundle JavaScript for new-pdp-v1
 * Handles dynamic pricing, variant selection, and cart functionality
 */


class ProductBundleNewPDPv1 {
  constructor() {
    // Look for our specific template by checking if it has our custom components
    this.container = document.querySelector('.product-single');
    if (!this.container) {
      console.log('ProductBundleNewPDPv1: Container not found'); // Debug log
      return;
    }
    
    // Check if already initialized
    if (this.container.dataset.bundleInitialized === 'true') {
      console.log('ProductBundleNewPDPv1: Already initialized, skipping'); // Debug log
      return;
    }
    
    // Check if this is our custom template by looking for our components
    const hasCustomComponents = this.container.querySelector('.size-selector-new-pdp-v1');
    if (!hasCustomComponents) {
      console.log('ProductBundleNewPDPv1: Not our custom template, skipping'); // Debug log
      return;
    }
    
    console.log('ProductBundleNewPDPv1: Initializing', this.container); // Debug log
    
    // Mark as initialized
    this.container.dataset.bundleInitialized = 'true';

    this.potSwiper = null;
    this.accessoriesSwiper = null;
    this.selectedPot = null;
    this.selectedSaucer = null;
    this.selectedAccessories = [];

    this.init();
  }

  init() {
    this.bindEvents();
    this.initializeState();
    this.initializeSwipers();
    this.updatePricing();
    this.addSpaceAfterColons();
  }

  initializeSwipers() {
    // Initialize Pot Swiper
    const potSwiperEl = this.container.querySelector('[data-pot-swiper]');
    if (potSwiperEl) {
      this.potSwiper = new Swiper(potSwiperEl, {
        slidesPerView: 'auto',
        spaceBetween: 0,
        navigation: {
          nextEl: '.pot-selector__nav--next',
          prevEl: '.pot-selector__nav--prev',
        }
      });
    }

    // Initialize Accessories Swiper
    const accessoriesSwiperEl = this.container.querySelector('[data-accessories-swiper]');
    if (accessoriesSwiperEl) {
      this.accessoriesSwiper = new Swiper(accessoriesSwiperEl, {
        slidesPerView: 'auto',
        spaceBetween: 0,
        navigation: {
          nextEl: '.accessories-selector__nav--next',
          prevEl: '.accessories-selector__nav--prev',
        }
      });
    }
  }

  bindEvents() {
    // Size selector events
    this.container.addEventListener('click', (e) => {
      console.log('Click detected:', e.target); // Debug log
      if (e.target.matches('.size-selector__button')) {
        console.log('Size button clicked:', e.target); // Debug log
        this.handleSizeSelection(e.target);
      }
    });

        // Pot selector events
        this.container.addEventListener('click', (e) => {
          if (e.target.closest('.pot-selector__item')) {
            this.handlePotSelection(e.target.closest('.pot-selector__item'));
          }
        });

    // Saucer toggle events
    this.container.addEventListener('click', (e) => {
      if (e.target.matches('.saucer-toggle__option')) {
        this.handleSaucerToggle(e.target);
      }
    });

    // Accessories selector events
    this.container.addEventListener('click', (e) => {
      if (e.target.closest('.accessories-selector__item')) {
        this.handleAccessorySelection(e.target.closest('.accessories-selector__item'));
      }
    });

    // Bundle add to cart
    this.container.addEventListener('click', (e) => {
      if (e.target.matches('[data-bundle-add-to-cart-btn]')) {
        this.handleAddToCart(e);
      }
    });

    // Slider navigation
    this.container.addEventListener('click', (e) => {
      if (e.target.matches('[data-pot-nav-prev]')) {
        this.navigateSlider('pot', 'prev');
      }
      if (e.target.matches('[data-pot-nav-next]')) {
        this.navigateSlider('pot', 'next');
      }
      if (e.target.matches('[data-accessories-nav-prev]')) {
        this.navigateSlider('accessories', 'prev');
      }
      if (e.target.matches('[data-accessories-nav-next]')) {
        this.navigateSlider('accessories', 'next');
      }
    });

    // Listen for variant changes from existing theme
    document.addEventListener('variant:change', (e) => {
      this.handleVariantChange(e.detail.variant);
    });

    // Listen for variant price changes from theme's Product class
    this.container.addEventListener('variantPriceChange', (e) => {
      if (e.detail && e.detail.variant) {
        this.updateStandardPriceBlock(e.detail.variant);
      }
    });
  }

  initializeState() {
    // Get current variant from URL parameter
    const urlParams = new URLSearchParams(window.location.search);
    const variantId = urlParams.get('variant');
    
    if (variantId) {
      // Find the variant data
      const productVariants = JSON.parse(this.container.dataset.productVariants);
      const currentVariant = productVariants.find(variant => variant.id.toString() === variantId);
      
      if (currentVariant) {
        // Update size selector UI based on current variant
        this.updateSizeSelectorFromVariant(currentVariant);
        
        // Update size label
        this.updateSizeLabelFromVariant(currentVariant);
      }
    } else {
      // Fallback to default behavior
      this.currentSize = this.getCurrentSize();
      this.updateSizeLabel();
    }

    // Initialize pot selector
    this.selectedPot = this.getDefaultPot();
    this.updatePotDisplay();

    // Initialize saucer toggle
    this.selectedSaucer = null;
    this.updateSaucerDisplay();
    
    // Update saucer availability based on selected pot (free pot = disabled saucer)
    this.updateSaucerAvailability();

    // Initialize accessories
    this.selectedAccessories = ['none']; // Start with "no accessory"
    this.updateAccessoriesDisplay();

    // Initialize pricing
    this.updatePricing();
  }

  updateSizeSelectorFromVariant(variant) {
    // Clear all size button selections
    this.container.querySelectorAll('.size-selector__button').forEach(btn => {
      btn.classList.remove('size-selector__button--selected');
    });
    
    // Find and select the button that matches this variant
    const sizeButtons = this.container.querySelectorAll('[data-size-value]');
    sizeButtons.forEach(button => {
      if (button.dataset.sizeValue === variant.options[0]) {
        button.classList.add('size-selector__button--selected');
      }
    });
  }

  updateSizeLabelFromVariant(variant) {
    const sizeLabel = this.container.querySelector('[data-current-size-label]');
    if (sizeLabel && variant.options[0]) {
      // Extract last 4 characters (e.g., "25cm" from "S - 25cm")
      const numericalValue = variant.options[0].slice(-5);
      sizeLabel.textContent = `– ${numericalValue}`;
    }
  }

  getCurrentSize() {
    const sizeButtons = this.container.querySelectorAll('[data-size-value]');
    for (let button of sizeButtons) {
      if (button.classList.contains('size-selector__button--selected')) {
        return button.dataset.sizeValue;
      }
    }
    return null;
  }

  getDefaultPot() {
    const pots = this.container.querySelectorAll('[data-pot-id]');
    for (let pot of pots) {
      if (pot.dataset.potPrice === '0') {
        return {
          id: pot.dataset.potId,
          price: parseInt(pot.dataset.potPrice),
          title: pot.dataset.potTitle
        };
      }
    }
    return null;
  }

      handleSizeSelection(button) {
        console.log('handleSizeSelection called with:', button); // Debug log
        const sizeValue = button.dataset.sizeValue;
        const sizeIndex = parseInt(button.dataset.sizeIndex);
        
        console.log('Size value:', sizeValue, 'Size index:', sizeIndex); // Debug log
        
        // Update size selector UI
        this.container.querySelectorAll('.size-selector__button').forEach(btn => {
          btn.classList.remove('size-selector__button--selected');
        });
        button.classList.add('size-selector__button--selected');
        
        // Update size label
        const sizeLabel = this.container.querySelector('[data-current-size-label]');
        if (sizeLabel) {
          // Extract last 4 characters (e.g., "25cm" from "S - 25cm")
          const numericalValue = sizeValue.slice(-5);
          sizeLabel.textContent = `– ${numericalValue}`;
        }
        
        // Find the corresponding variant by matching the size value
        const variantInputs = this.container.querySelectorAll('input[data-variant-input]');
        let targetVariantId = null;
        
        // Get all variants from the product data
        const productVariants = JSON.parse(this.container.dataset.productVariants);
        
        // Find the variant that matches the selected size
        const matchingVariant = productVariants.find(variant => {
          return variant.options[0] === sizeValue; // First option is the size
        });
        
        if (matchingVariant) {
          console.log('Found matching variant:', matchingVariant);
          targetVariantId = matchingVariant.id;
          
          // Update the hidden variant input
          variantInputs.forEach(input => {
            input.checked = input.value === targetVariantId.toString();
          });
          
          // Update URL without reloading
          this.updateURL(targetVariantId);
          
          // Trigger theme's variant change event
          this.triggerVariantChange(matchingVariant);
          
          // Update pricing
          this.updatePricing();
          
          // Update standard price block
          this.updateStandardPriceBlock(matchingVariant);
          
          // Reset pot and saucer selections since variant changed
          this.resetPotAndSaucer();
          
          // Reload pot selector with new variant's metafield data
          this.reloadPotSelector(matchingVariant);
          
          // Reload accessories selector with new variant's metafield data
          this.reloadAccessoriesSelector(matchingVariant);
        } else {
          console.log('No matching variant found for size:', sizeValue);
        }
      }

  updateSizeLabel() {
    const label = this.container.querySelector('[data-current-size-label]');
    if (label && this.currentSize) {
      // Extract last 4 characters (e.g., "25cm" from "S - 25cm")
      const numericalValue = this.currentSize.slice(-5);
      label.textContent = `– ${numericalValue}`;
    }
  }

      triggerVariantChange(variant) {
        // Trigger the theme's variant change event
        const variantChangeEvent = new CustomEvent('variant:change', {
          detail: { variant: variant }
        });
        document.dispatchEvent(variantChangeEvent);
        
        // Also trigger the standard variant change for theme compatibility
        const productSelect = this.container.querySelector('[data-product-select]');
        if (productSelect) {
          productSelect.value = variant.id;
          productSelect.dispatchEvent(new Event('change'));
        }
      }

      updateURL(variantId) {
        if (variantId) {
          const url = new URL(window.location);
          url.searchParams.set('variant', variantId);
          window.history.replaceState({}, '', url);
        }
      }

      resetPotAndSaucer() {
        // Reset pot selection to first free pot
        this.container.querySelectorAll('.pot-selector__item').forEach(item => {
          item.classList.remove('pot-selector__item--selected');
        });
        
        const firstFreePot = this.container.querySelector('.pot-selector__item[data-pot-price="0"]');
        if (firstFreePot) {
          firstFreePot.classList.add('pot-selector__item--selected');
          this.selectedPot = {
            id: firstFreePot.dataset.potId,
            price: 0,
            title: firstFreePot.dataset.potTitle
          };
        }
        
        // Reset saucer to "Non"
        this.container.querySelectorAll('.saucer-toggle__option').forEach(option => {
          option.classList.remove('saucer-toggle__option--selected');
        });
        
        const noSaucerOption = this.container.querySelector('.saucer-toggle__option[data-saucer-option="no"]');
        if (noSaucerOption) {
          noSaucerOption.classList.add('saucer-toggle__option--selected');
          this.selectedSaucer = null;
        }
        
        // Reset accessories to "No accessory"
        this.container.querySelectorAll('.accessories-selector__item').forEach(item => {
          item.classList.remove('accessories-selector__item--selected');
        });
        
        const noAccessoryItem = this.container.querySelector('.accessories-selector__item--no-accessory');
        if (noAccessoryItem) {
          noAccessoryItem.classList.add('accessories-selector__item--selected');
          this.selectedAccessories = ['none'];
        }
        
        // Update displays
        this.updatePotDisplay();
        this.updateSaucerDisplay();
        this.updateSaucerAvailability();
        this.updateAccessoriesDisplay();
      }

  reloadPotSelector(variant) {
    console.log('reloadPotSelector called with variant:', variant);
    this.reloadPotSelectorViaAjax(variant.id);
  }

  reloadAccessoriesSelector(variant) {
    console.log('reloadAccessoriesSelector called with variant:', variant);
    
    // Use the AJAX approach to reload accessories selector
    console.log('Using AJAX approach to reload accessories selector');
    this.reloadAccessoriesSelectorViaAjax(variant.id);
  }

  createPotItems(swiperWrapper, pots) {
    // Clear existing pot items
    swiperWrapper.innerHTML = '';
    
    // Create new pot items
    pots.forEach((pot, index) => {
      const potItem = document.createElement('div');
      potItem.className = `swiper-slide pot-selector__item${index === 0 ? ' pot-selector__item--selected' : ''}`;
      potItem.setAttribute('data-pot-id', pot.id);
      potItem.setAttribute('data-pot-price', pot.price);
      potItem.setAttribute('data-pot-title', pot.title);
      
      // Create image
      const imageDiv = document.createElement('div');
      imageDiv.className = 'pot-selector__image';
      
      if (pot.featured_image) {
        const img = document.createElement('img');
        img.src = pot.featured_image;
        img.alt = pot.title;
        img.loading = 'lazy';
        imageDiv.appendChild(img);
      } else {
        const placeholder = document.createElement('div');
        placeholder.className = 'pot-selector__image-placeholder';
        const text = document.createElement('span');
        text.className = 'pot-selector__placeholder-text';
        text.textContent = pot.title.substring(0, 20);
        placeholder.appendChild(text);
        imageDiv.appendChild(placeholder);
      }
      
      // Create price
      const priceDiv = document.createElement('div');
      priceDiv.className = 'pot-selector__price';
      
      if (pot.price === 0) {
        const freeSpan = document.createElement('span');
        freeSpan.className = 'pot-selector__price-free';
        freeSpan.textContent = '+ 0,00€';
        priceDiv.appendChild(freeSpan);
      } else {
        priceDiv.textContent = `+ ${(pot.price / 100).toFixed(2)}€`;
      }
      
      // Assemble the pot item
      potItem.appendChild(imageDiv);
      potItem.appendChild(priceDiv);
      
      // Add to swiper wrapper
      swiperWrapper.appendChild(potItem);
    });
    
    // Update selected pot to first pot
    if (pots.length > 0) {
      this.selectedPot = {
        id: pots[0].id,
        price: pots[0].price,
        title: pots[0].title
      };
      
      // Update pot display
      this.updatePotDisplay();
    }
    
    // Reinitialize Swiper if it exists
    if (this.potSwiper) {
      this.potSwiper.update();
    }
  }

  reloadPotSelectorViaAjax(variantId) {
    console.log('Reloading pot selector via AJAX for variant:', variantId);
    
    const currentUrl = new URL(window.location);
    currentUrl.searchParams.set('variant', variantId);
    
    fetch(currentUrl.toString())
      .then(response => response.text())
      .then(html => {
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        
        const newPotSelector = doc.querySelector('.pot-selector__slider-container');
        if (newPotSelector) {
          const currentPotSelector = this.container.querySelector('.pot-selector__slider-container');
          if (currentPotSelector) {
            currentPotSelector.innerHTML = newPotSelector.innerHTML;
            
            // Destroy and reinitialize Swiper
            if (this.potSwiper) {
              this.potSwiper.destroy(true, true);
            }
            
            const potSwiperEl = this.container.querySelector('[data-pot-swiper]');
            if (potSwiperEl) {
              this.potSwiper = new Swiper(potSwiperEl, {
                slidesPerView: 'auto',
                spaceBetween: 0,
                navigation: {
                  nextEl: '.pot-selector__nav--next',
                  prevEl: '.pot-selector__nav--prev',
                }
              });
            }
            
            // Reset pot selection to first pot
            const firstPot = this.container.querySelector('.pot-selector__item');
            if (firstPot) {
              this.handlePotSelection(firstPot);
            }
            
            console.log('Pot selector reloaded successfully');
          }
        }
      })
      .catch(error => {
        console.error('Error reloading pot selector:', error);
      });
  }

  reloadAccessoriesSelectorViaAjax(variantId) {
    console.log('Reloading accessories selector via AJAX for variant:', variantId);
    
    // Get the current URL and add variant parameter
    const currentUrl = new URL(window.location);
    currentUrl.searchParams.set('variant', variantId);
    
    // Make AJAX request to get the updated accessories selector
    fetch(currentUrl.toString())
      .then(response => response.text())
      .then(html => {
        // Parse the HTML response
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        
        // Find the new accessories selector
        const newAccessoriesSelector = doc.querySelector('.accessories-selector__slider-container');
        if (newAccessoriesSelector) {
          // Replace the current accessories selector
          const currentAccessoriesSelector = this.container.querySelector('.accessories-selector__slider-container');
          if (currentAccessoriesSelector) {
            currentAccessoriesSelector.innerHTML = newAccessoriesSelector.innerHTML;
            
            // Destroy old Swiper instance
            if (this.accessoriesSwiper) {
              this.accessoriesSwiper.destroy(true, true);
            }
            
            // Wait for DOM to be fully rendered and layout to be calculated
            setTimeout(() => {
              // Reinitialize Swiper
              const accessoriesSwiperEl = this.container.querySelector('[data-accessories-swiper]');
              if (accessoriesSwiperEl) {
                // Force layout recalculation by reading offsetHeight
                const forceLayout = accessoriesSwiperEl.offsetHeight;
                
                this.accessoriesSwiper = new Swiper(accessoriesSwiperEl, {
                  slidesPerView: 'auto',
                  spaceBetween: 0,
                  observer: true,
                  observeParents: true,
                  observeSlideChildren: true,
                  navigation: {
                    nextEl: '.accessories-selector__nav--next',
                    prevEl: '.accessories-selector__nav--prev',
                  }
                });
                
                // Force immediate update after initialization
                this.accessoriesSwiper.update();
              }
              
              // Reset accessories selection to "No accessory"
              const noAccessoryItem = this.container.querySelector('.accessories-selector__item--no-accessory');
              if (noAccessoryItem) {
                this.handleAccessorySelection(noAccessoryItem);
              }
              
              console.log('Accessories selector reloaded successfully');
            }, 100);
          }
        }
      })
      .catch(error => {
        console.error('Error reloading accessories selector:', error);
      });
  }

  updateVariantSelection() {
    // Find matching variant based on size
    const variants = JSON.parse(this.container.dataset.productVariants);
    const matchingVariant = variants.find(variant => {
      return variant.options[0] === this.currentSize;
    });

    if (matchingVariant) {
      // Update the hidden select
      const select = this.container.querySelector('[data-product-select]');
      if (select) {
        select.value = matchingVariant.id;
        select.dispatchEvent(new Event('change'));
      }
    }
  }

      handlePotSelection(potItem) {
        // Update selected state
        this.container.querySelectorAll('.pot-selector__item').forEach(item => {
          item.classList.remove('pot-selector__item--selected');
        });
        potItem.classList.add('pot-selector__item--selected');

        // Update selected pot
        this.selectedPot = {
          id: potItem.dataset.potId,
          price: parseInt(potItem.dataset.potPrice),
          title: potItem.dataset.potTitle
        };

        // Update selected pot display text
        const selectedPotDisplay = this.container.querySelector('[data-selected-pot-display-text]');
        if (selectedPotDisplay) {
          selectedPotDisplay.textContent = potItem.dataset.potTitle || '';
        }

        // If the selected pot is free, automatically reset saucer to "Non"
        if (this.selectedPot.price === 0) {
          // Deselect all saucer options
          this.container.querySelectorAll('[data-saucer-option]').forEach(opt => {
            opt.classList.remove('saucer-toggle__option--selected');
          });
          
          // Select "Non" option
          const saucerNoButton = this.container.querySelector('[data-saucer-option="no"]');
          if (saucerNoButton) {
            saucerNoButton.classList.add('saucer-toggle__option--selected');
          }
          
          // Clear selected saucer
          this.selectedSaucer = null;
        }

        this.updatePotDisplay();
        this.updateSaucerAvailability();
        this.updatePricing();
      }

  updatePotDisplay() {
    // Update the selected pot display text in the title
    const selectedPotDisplay = this.container.querySelector('[data-selected-pot-display-text]');
    if (selectedPotDisplay && this.selectedPot) {
      selectedPotDisplay.textContent = this.selectedPot.title || '';
    }
  }

  updateSaucerAvailability() {
    const saucerBlock = this.container.querySelector('.saucer-toggle-new-pdp-v1');
    const saucerYesButton = this.container.querySelector('[data-saucer-option="yes"]');
    const saucerNoButton = this.container.querySelector('[data-saucer-option="no"]');
    const hideSaucer = this.selectedPot && this.selectedPot.price === 0;

    // Hide/show the entire saucer selector when pot is free/paid
    if (saucerBlock) {
      saucerBlock.style.display = hideSaucer ? 'none' : '';
    }

    // Maintain button enabled state if visible
    if (saucerYesButton) {
      if (hideSaucer) {
        saucerYesButton.disabled = true;
        saucerYesButton.classList.add('saucer-toggle__option--disabled');
        // Force selection to "Non" and clear state
        if (saucerNoButton) {
          this.container.querySelectorAll('[data-saucer-option]').forEach(btn => btn.classList.remove('saucer-toggle__option--selected'));
          saucerNoButton.classList.add('saucer-toggle__option--selected');
        }
        this.selectedSaucer = null;
        this.updatePricing();
      } else {
        saucerYesButton.disabled = false;
        saucerYesButton.classList.remove('saucer-toggle__option--disabled');
      }
    }
  }

  handleSaucerToggle(button) {
    const option = button.dataset.saucerOption;

    // Update selected state
    this.container.querySelectorAll('[data-saucer-option]').forEach(btn => {
      btn.classList.remove('saucer-toggle__option--selected');
    });
    button.classList.add('saucer-toggle__option--selected');

    if (option === 'yes') {
      this.selectedSaucer = {
        id: button.dataset.saucerId,
        price: parseInt(button.dataset.saucerPrice),
        title: button.dataset.saucerTitle
      };
    } else {
      this.selectedSaucer = null;
    }

    this.updateSaucerDisplay();
    this.updatePricing();
  }

  updateSaucerDisplay() {
    // Saucer display is handled by the toggle buttons themselves
    // No separate display element needed in our simplified template
  }

  updateAccessoriesDisplay() {
    // Update the accessories count in the title
    const countElement = this.container.querySelector('[data-accessories-count]');
    if (countElement && this.selectedAccessories) {
      if (this.selectedAccessories.includes('none') || this.selectedAccessories.length === 0) {
        countElement.textContent = '(0)';
      } else {
        const selectedCount = this.selectedAccessories.length;
        countElement.textContent = `(${selectedCount})`;
      }
    }
    
    // Update the accessories list display
    const list = this.container.querySelector('[data-selected-accessories-list]');
    if (list) {
      list.innerHTML = '';

      if (this.selectedAccessories.includes('none')) {
        list.innerHTML = '<span class="accessories-selector__selected-item">Aucun accessoire</span>';
      } else {
        this.selectedAccessories.forEach(accessoryId => {
          const accessoryItem = this.container.querySelector(`[data-accessory-id="${accessoryId}"]`);
          if (accessoryItem) {
            const name = accessoryItem.dataset.accessoryTitle;
            const price = parseInt(accessoryItem.dataset.accessoryPrice);
            const item = document.createElement('span');
            item.className = 'accessories-selector__selected-item';
            item.textContent = `${name} (${price === 0 ? 'Gratuit' : this.formatMoney(price)})`;
            list.appendChild(item);
          }
        });
      }
    }
  }

  handleAccessorySelection(accessoryItem) {
    const accessoryId = accessoryItem.dataset.accessoryId;
    const accessoryPrice = parseInt(accessoryItem.dataset.accessoryPrice);
    
    // Handle "No accessory" selection
    if (accessoryId === 'none') {
      // Deselect all other accessories
      this.container.querySelectorAll('.accessories-selector__item').forEach(item => {
        item.classList.remove('accessories-selector__item--selected');
      });
      
      // Select "No accessory"
      accessoryItem.classList.add('accessories-selector__item--selected');
      this.selectedAccessories = ['none'];
      
      console.log('Selected "No accessory"');
    } else {
      // Handle regular accessory selection
      if (accessoryItem.classList.contains('accessories-selector__item--selected')) {
        // Deselect the accessory
        accessoryItem.classList.remove('accessories-selector__item--selected');
        this.selectedAccessories = this.selectedAccessories.filter(id => id !== accessoryId);
        
        console.log('Deselected accessory:', accessoryId);
        
        // If no accessories selected, select "No accessory"
        if (this.selectedAccessories.length === 0) {
          const noAccessoryItem = this.container.querySelector('.accessories-selector__item--no-accessory');
          if (noAccessoryItem) {
            noAccessoryItem.classList.add('accessories-selector__item--selected');
            this.selectedAccessories = ['none'];
            console.log('Auto-selected "No accessory"');
          }
        }
      } else {
        // Select the accessory
        accessoryItem.classList.add('accessories-selector__item--selected');
        
        // Remove "No accessory" from selection
        const noAccessoryItem = this.container.querySelector('.accessories-selector__item--no-accessory');
        if (noAccessoryItem) {
          noAccessoryItem.classList.remove('accessories-selector__item--selected');
        }
        
        // Add to selected accessories
        if (!this.selectedAccessories.includes(accessoryId)) {
          this.selectedAccessories.push(accessoryId);
        }
        
        // Remove 'none' from selected accessories if it exists
        this.selectedAccessories = this.selectedAccessories.filter(id => id !== 'none');
        
        console.log('Selected accessory:', accessoryId);
      }
    }
    
    console.log('Current selected accessories:', this.selectedAccessories);
    
    // Update displays
    this.updateAccessoriesDisplay();
    this.updatePricing();
  }

  updateAccessoryButtons() {
    // This method is no longer needed since we removed the select buttons
    // Accessories are now selected by clicking the item directly
  }

  updatePricing() {
    let totalPrice = 0;

    // Main product price
    const mainProductPrice = this.getMainProductPrice();
    totalPrice += mainProductPrice;

    // Pot price
    if (this.selectedPot) {
      totalPrice += this.selectedPot.price;
    }

    // Saucer price
    if (this.selectedSaucer) {
      totalPrice += this.selectedSaucer.price;
    }

    // Accessories price
    if (!this.selectedAccessories.includes('none')) {
      this.selectedAccessories.forEach(accessoryId => {
        const accessoryItem = this.container.querySelector(`[data-accessory-id="${accessoryId}"]`);
        if (accessoryItem) {
          totalPrice += parseInt(accessoryItem.dataset.accessoryPrice);
        }
      });
    }

    // Update price displays
    this.updatePriceSummary(totalPrice);
    this.updateAddToCartButton(totalPrice);
    this.addSpaceAfterColons();
  }

  getMainProductPrice() {
    const select = this.container.querySelector('[data-product-select]');
    if (select && select.value) {
      const variants = JSON.parse(this.container.dataset.productVariants);
      const variant = variants.find(v => v.id == select.value);
      return variant ? variant.price : 0;
    }
    return 0;
  }

  updatePriceSummary(totalPrice) {
    // Update main product price
    const mainPrice = this.container.querySelector('[data-main-product-price]');
    if (mainPrice) {
      mainPrice.textContent = this.formatMoney(this.getMainProductPrice());
    }

    // Update pot price
    const potItem = this.container.querySelector('.bundle-price-summary__item--pot');
    const potPrice = this.container.querySelector('.bundle-price-summary [data-pot-price]');
    if (potItem && potPrice) {
      if (this.selectedPot) {
        potItem.style.display = 'flex';
        potPrice.textContent = this.selectedPot.price === 0 ? 'Gratuit' : this.formatMoney(this.selectedPot.price);
      } else {
        potItem.style.display = 'none';
      }
    }

    // Update saucer price
    const saucerItem = this.container.querySelector('.bundle-price-summary__item--saucer');
    const saucerPrice = this.container.querySelector('.bundle-price-summary [data-saucer-price]');
    if (saucerItem && saucerPrice) {
      if (this.selectedSaucer) {
        saucerItem.style.display = 'flex';
        saucerPrice.textContent = this.formatMoney(this.selectedSaucer.price);
      } else {
        saucerItem.style.display = 'none';
      }
    }

    // Update accessories price
    const accessoriesItem = this.container.querySelector('.bundle-price-summary__item--accessories');
    const accessoriesPrice = this.container.querySelector('.bundle-price-summary [data-accessories-price]');
    if (accessoriesItem && accessoriesPrice) {
      let accessoriesTotal = 0;
      if (!this.selectedAccessories.includes('none')) {
        this.selectedAccessories.forEach(accessoryId => {
          const accessoryItem = this.container.querySelector(`[data-accessory-id="${accessoryId}"]`);
          if (accessoryItem) {
            accessoriesTotal += parseInt(accessoryItem.dataset.accessoryPrice);
          }
        });
      }
      
      // Only show accessories line if accessories are selected
      if (!this.selectedAccessories.includes('none') && this.selectedAccessories.length > 0) {
        accessoriesItem.style.display = 'flex';
        if (accessoriesTotal > 0) {
          accessoriesPrice.textContent = this.formatMoney(accessoriesTotal);
        } else {
          accessoriesPrice.textContent = 'Gratuit';
        }
      } else {
        accessoriesItem.style.display = 'none';
      }
    }

    // Update total price
    const totalPriceElement = this.container.querySelector('[data-total-price]');
    if (totalPriceElement) {
      totalPriceElement.textContent = this.formatMoney(totalPrice);
    }
  }

  updateAddToCartButton(totalPrice) {
    const button = this.container.querySelector('[data-bundle-add-to-cart-btn]');
    const priceElement = this.container.querySelector('[data-button-price]');
    
    if (priceElement && totalPrice !== undefined) {
      priceElement.textContent = this.formatMoney(totalPrice);
    }
  }

  async handleAddToCart(e) {
    e.preventDefault();
    e.stopPropagation(); // Prevent theme's default add-to-cart handler
    
    const button = e.target;
    const loadingState = this.container.querySelector('[data-loading-state]');
    const successState = this.container.querySelector('[data-success-state]');
    const errorState = this.container.querySelector('[data-error-state]');

    // Show loading state
    button.disabled = true;
    if (loadingState) loadingState.style.display = 'block';

    try {
      // Debug: Log current state
      console.log('Current selections:', {
        selectedPot: this.selectedPot,
        selectedSaucer: this.selectedSaucer,
        selectedAccessories: this.selectedAccessories
      });
      
      // Prepare cart items (add in reverse visual order so drawer shows Plant first)
      const items = [];

      // 1) Accessories first (so they appear last)
      if (!this.selectedAccessories.includes('none')) {
        console.log('Adding accessories:', this.selectedAccessories);
        this.selectedAccessories.forEach(accessoryId => {
          const accessoryItem = this.container.querySelector(`[data-accessory-id="${accessoryId}"]`);
          if (accessoryItem) {
            items.push({ id: parseInt(accessoryId), quantity: 1, properties: {} });
          }
        });
      }

      // 2) Saucer next (if any)
      if (this.selectedSaucer) {
        console.log('Adding saucer:', this.selectedSaucer);
        items.push({ id: parseInt(this.selectedSaucer.id), quantity: 1, properties: {} });
      }

      // 3) Pot next (even if free)
      if (this.selectedPot) {
        console.log('Adding pot:', this.selectedPot);
        items.push({ id: parseInt(this.selectedPot.id), quantity: 1, properties: {} });
      }

      // 4) Main product last (so it shows first)
      const select = this.container.querySelector('[data-product-select]');
      if (select && select.value) {
        console.log('Adding main product:', select.value);
        items.push({ id: select.value, quantity: 1, properties: {} });
      } else {
        console.warn('No product select found or no value');
      }
      
      console.log('Final items to add to cart:', items);
      console.log('Stringified request body:', JSON.stringify({ items: items }));

      // Add to cart
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          items: items
        })
      });

      if (response.ok) {
        const result = await response.json();
        console.log('Successfully added to cart:', result);
        
        // Trigger the same event the theme uses for cart updates
        const form = this.container.querySelector('form[action*="/cart/add"]');
        if (form) {
          form.dispatchEvent(new CustomEvent('ajaxProduct:added', {
            detail: { 
              product: result,
              addToCartBtn: button
            },
            bubbles: true
          }));
        }
        
        // Show success state
        if (loadingState) loadingState.style.display = 'none';
        if (successState) successState.style.display = 'block';
        
        setTimeout(() => {
          if (successState) successState.style.display = 'none';
          button.disabled = false;
        }, 2000);
        
      } else {
        const errorData = await response.json();
        console.error('Cart API error response:', errorData);
        throw new Error(errorData.description || 'Failed to add items to cart');
      }

    } catch (error) {
      console.error('Error adding to cart:', error);
      
      // Show error state
      if (loadingState) loadingState.style.display = 'none';
      if (errorState) {
        errorState.style.display = 'block';
        const errorMessage = errorState.querySelector('[data-error-message]');
        if (errorMessage) {
          errorMessage.textContent = 'Erreur lors de l\'ajout au panier. Veuillez réessayer.';
        }
      }
      
      // Hide error state after 3 seconds
      setTimeout(() => {
        if (errorState) errorState.style.display = 'none';
        button.disabled = false;
      }, 3000);
    }
  }

  navigateSlider(type, direction) {
    const slider = this.container.querySelector(`[data-${type}-slider]`);
    if (!slider) return;

    const itemWidth = 200; // Adjust based on your CSS
    const currentScroll = slider.scrollLeft;
    const scrollAmount = itemWidth * (direction === 'next' ? 1 : -1);
    
    slider.scrollTo({
      left: currentScroll + scrollAmount,
      behavior: 'smooth'
    });
  }

  formatMoney(cents) {
    // Format as "XX,XX €" to match the mockup
    const euros = (cents / 100).toFixed(2);
    return euros.replace('.', ',') + ' €';
  }

  // Ensure a space appears before every colon within this template only
  addSpaceAfterColons() {
    if (!this.container) return;
    const walker = document.createTreeWalker(this.container, NodeFilter.SHOW_TEXT, null);
    const toChange = [];
    while (walker.nextNode()) {
      const node = walker.currentNode;
      if (!node.nodeValue) continue;
      const original = node.nodeValue;
      // Ensure exactly one space before colon when missing
      const updated = original.replace(/(\S)\s*:(\S)/g, '$1 :$2');
      if (updated !== original) toChange.push({ node, updated });
    }
    toChange.forEach(({ node, updated }) => { node.nodeValue = updated; });
  }

  handleVariantChange(variant) {
    if (variant) {
      this.updatePricing();
      this.updateStandardPriceBlock(variant);
    }
  }

  updateStandardPriceBlock(variant) {
    if (!variant) {
      console.log('updateStandardPriceBlock: variant not provided');
      return;
    }

    // Find the visible price block within product-blocks, not in hidden divs
    const productBlocks = this.container.querySelector('.product-blocks--new-pdp-v1') || this.container.querySelector('.product-blocks');
    if (!productBlocks) {
      console.log('updateStandardPriceBlock: product-blocks not found');
      return;
    }

    // Find the price block that's not hidden
    const priceBlocks = productBlocks.querySelectorAll('.product-block--price');
    let priceBlock = null;
    for (let block of priceBlocks) {
      // Check if this block is visible (not in a hidden parent)
      let parent = block.parentElement;
      let isHidden = false;
      while (parent && parent !== document.body) {
        if (parent.style.display === 'none' || window.getComputedStyle(parent).display === 'none') {
          isHidden = true;
          break;
        }
        parent = parent.parentElement;
      }
      if (!isHidden) {
        priceBlock = block;
        break;
      }
    }

    // Fallback: just get the first price block in product-blocks
    if (!priceBlock && priceBlocks.length > 0) {
      priceBlock = priceBlocks[0];
    }

    if (!priceBlock) {
      console.log('updateStandardPriceBlock: visible price block not found');
      return;
    }

    const priceElement = priceBlock.querySelector('[data-product-price][data-top-price]') || priceBlock.querySelector('[data-product-price]');
    const comparePriceElement = priceBlock.querySelector('[data-compare-price]');
    const priceWrapper = priceBlock.querySelector('[data-product-price-wrap]');
    const comparePriceA11y = priceBlock.querySelector('[data-compare-price-a11y]');
    const priceA11y = priceBlock.querySelector('[data-a11y-price]');

    if (!priceElement) {
      console.log('updateStandardPriceBlock: priceElement not found in priceBlock', priceBlock);
      return;
    }

    console.log('updateStandardPriceBlock: Updating price for variant', variant.id, 'Price:', variant.price, 'Compare:', variant.compare_at_price);

    // Update main price using theme's formatMoney if available, otherwise use our format
    const formatPrice = (cents) => {
      if (window.theme && window.theme.Currency && window.theme.Currency.formatMoney) {
        return window.theme.Currency.formatMoney(cents, window.theme.settings.moneyFormat);
      }
      return this.formatMoney(cents);
    };

    // Update main price
    const formattedPrice = formatPrice(variant.price);
    console.log('updateStandardPriceBlock: Setting price to', formattedPrice);
    priceElement.textContent = formattedPrice;

    // Update compare price if it exists
    if (variant.compare_at_price && variant.compare_at_price > variant.price) {
      const formattedComparePrice = formatPrice(variant.compare_at_price);
      console.log('updateStandardPriceBlock: Setting compare price to', formattedComparePrice);
      if (comparePriceElement) {
        comparePriceElement.textContent = formattedComparePrice;
      }
      if (priceWrapper) {
        priceWrapper.classList.remove('hide');
      }
      if (priceElement) {
        priceElement.classList.add('on-sale');
      }
      if (comparePriceA11y) {
        comparePriceA11y.setAttribute('aria-hidden', 'false');
      }
      if (priceA11y) {
        priceA11y.setAttribute('aria-hidden', 'false');
      }
    } else {
      // No compare price, hide compare price elements
      console.log('updateStandardPriceBlock: No compare price, hiding compare elements');
      if (priceWrapper) {
        priceWrapper.classList.add('hide');
      }
      if (priceElement) {
        priceElement.classList.remove('on-sale');
      }
      if (comparePriceA11y) {
        comparePriceA11y.setAttribute('aria-hidden', 'true');
      }
      if (priceA11y) {
        priceA11y.setAttribute('aria-hidden', 'true');
      }
    }
  }
}

// Initialize when DOM is ready or if it's already loaded
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new ProductBundleNewPDPv1();
  });
} else {
  new ProductBundleNewPDPv1();
}
